import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'

import { useCreateTerminalOrganisation } from '@dhl/mdp-api'
import { Box, Card } from '@dhl-ui/components'
import { BackButton } from '~/temp-ds-component/BackButton'

import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { getErrorMessage } from '~/common/util/axiosUtil'

import { TerminalForm, CancelDialog } from '~/modules/terminals/components'

import { createValidationSchema, convertToTerminalDto, initialTerminal, type Terminal } from '~/modules/terminals/model'
import { useAddressService } from '~/common/components/addresses'

export const AddTerminalPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)

  const { mutateAsync: createTerminal } = useCreateTerminalOrganisation()

  const { addressValidationSchema, updateAddressValidationSchema } = useAddressService(
    initialTerminal?.data.address.countryCode
  )
  const validationSchema = createValidationSchema(t, addressValidationSchema)

  const onChangeCountry = async (countryCode: string) => {
    updateAddressValidationSchema(countryCode)
  }

  const onSubmit = async (newTerminal: Terminal) => {
    const newTerminalDto = convertToTerminalDto(newTerminal)
    try {
      await createTerminal({ data: newTerminalDto })
      showSuccess(t('admin.terminalsPage.addTerminalSuccess'))
      navigate(links.terminalsLink())
    } catch (error) {
      const message = getErrorMessage(error, t('admin.terminalsPage.addTerminalError'))
      showError(message)
    }
  }

  const onBack = (dirty: boolean) => (dirty ? setCancelDialogOpen(true) : navigate(links.terminalsLink()))

  return (
    <>
      <Formik initialValues={initialTerminal} onSubmit={onSubmit} validateOnMount validationSchema={validationSchema}>
        {({ dirty }) => (
          <>
            <Box>
              <Box marginBottom={2}>
                <BackButton onClick={() => onBack(dirty)}>
                  {t('admin.terminalsPage.terminalForm.backButtonTitle')}
                </BackButton>
              </Box>
            </Box>
            <Card>
              <Card.Header title={t('admin.terminalsPage.terminalDetailsPage.titleText')} />
              <Card.Content>
                <TerminalForm
                  country={initialTerminal.data.address.countryCode}
                  type={'add'}
                  onCancel={() => navigate(links.terminalsLink())}
                  onChangeCountry={onChangeCountry}
                />
              </Card.Content>
            </Card>
          </>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  )
}
